import type { FC } from 'react'

import { SDS_SPACE_LARGE } from '@s-group/design-system-tokens/web/tokens/space.es6'
import { Carousel } from 'components/Carousel'
import { SectionTitle } from 'components/common/Titles'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useComplementaryProductsContext } from 'services/ComplementaryProducts/complementary-products-provider'
import styled from 'styled-components'
import { TrackingContextProvider } from 'utils/tracking/components/TrackingContextProvider'
import { useProductsImpressionTracking } from 'utils/tracking/hooks/use-products-impression-tracking'
import { TrackingContext } from 'utils/tracking/interfaces/data-layer-events'
import { StyledProductTitleWithCTA } from 'views/contentful/components/contentful-section/FeaturedProducts'

interface Props {
  className?: string
  focusOnEan: string | null
}

const LIST_NAME_PREFIX = 'complementary-products'

const _ComplementaryProducts: FC<Props> = ({ className, focusOnEan }) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLElement>(null)
  const { complementaryProductsQuery } = useComplementaryProductsContext()
  const { loading, data, error } = complementaryProductsQuery
  const complementaryProducts = data?.complementaryProducts || []

  const listName = `${LIST_NAME_PREFIX}: ${focusOnEan ?? 'cart'}`
  useProductsImpressionTracking({
    listName,
    trackingContext: TrackingContext.PRODUCT_DETAIL_PAGE,
    items: complementaryProducts,
    ref,
    shouldTrackCategory: false,
  })

  /**
   * Do not render in case of error, or no complementary products found.
   */
  if (error || (!loading && !complementaryProducts.length)) {
    return null
  }

  return (
    <TrackingContextProvider
      metadata={{ listName }}
      trackingContext={TrackingContext.PRODUCT_DETAIL_PAGE}
    >
      <article className={className} data-test-id={LIST_NAME_PREFIX} ref={ref}>
        {loading ? (
          <p>{t`Loading...`}</p>
        ) : (
          <>
            <StyledProductTitleWithCTA>
              <SectionTitle>{t`Often bought together`}</SectionTitle>
            </StyledProductTitleWithCTA>
            <TrackingContextProvider listName={listName}>
              <Carousel products={complementaryProducts} />
            </TrackingContextProvider>
          </>
        )}
      </article>
    </TrackingContextProvider>
  )
}

export const ComplementaryProducts = styled(_ComplementaryProducts)({
  marginTop: SDS_SPACE_LARGE,
})
